import React from 'react';
import { X } from '@phosphor-icons/react';
import {
  FilterOperator,
  UnitFilterCondition,
  FilterableFieldConfig,
} from 'shared/types/cluster';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SimpleDropdown, SimpleDropdownOption } from '@/components/ui/simple-dropdown';

const OPERATOR_LABELS: Record<string, string> = {
  equals: 'is gelijk aan',
  not_equals: 'is niet gelijk aan',
  contains: 'bevat',
  not_contains: 'bevat niet',
  starts_with: 'begint met',
  ends_with: 'eindigt met',
  in: 'is een van',
  not_in: 'is niet een van',
  greater_than: 'groter dan',
  greater_than_or_equal: 'groter dan of gelijk',
  less_than: 'kleiner dan',
  less_than_or_equal: 'kleiner dan of gelijk',
  between: 'tussen',
  is_null: 'is leeg',
  is_not_null: 'is niet leeg',
  is_empty: 'is leeg',
  is_not_empty: 'is niet leeg',
};

/** Operators that don't need a value input */
const NO_VALUE_OPERATORS = new Set([
  FilterOperator.IS_NULL,
  FilterOperator.IS_NOT_NULL,
  FilterOperator.IS_EMPTY,
  FilterOperator.IS_NOT_EMPTY,
]);

interface UnitFilterConditionRowProps {
  condition: UnitFilterCondition;
  fieldConfigs: FilterableFieldConfig[];
  onChange: (updated: UnitFilterCondition) => void;
  onRemove: () => void;
}

export const UnitFilterConditionRow: React.FC<UnitFilterConditionRowProps> = ({
  condition,
  fieldConfigs,
  onChange,
  onRemove,
}) => {
  const selectedFieldConfig = fieldConfigs.find(
    (fc) => fc.field === condition.field,
  );
  const availableOperators = selectedFieldConfig?.operators || [];
  const needsValue = !NO_VALUE_OPERATORS.has(condition.operator);
  const isBetween = condition.operator === FilterOperator.BETWEEN;

  const handleFieldChange = (field: string) => {
    const newFieldConfig = fieldConfigs.find((fc) => fc.field === field);
    const newOperators = newFieldConfig?.operators || [];
    const firstOperator = newOperators[0] || FilterOperator.EQUALS;
    onChange({
      ...condition,
      field,
      operator: firstOperator,
      value: null,
      valueEnd: undefined,
    });
  };

  const handleOperatorChange = (operator: string) => {
    const op = operator as FilterOperator;
    onChange({
      ...condition,
      operator: op,
      value: NO_VALUE_OPERATORS.has(op) ? null : condition.value,
      valueEnd: op === FilterOperator.BETWEEN ? condition.valueEnd : undefined,
    });
  };

  const handleValueChange = (value: string | number | boolean | null) => {
    onChange({ ...condition, value });
  };

  const handleValueEndChange = (valueEnd: string | number) => {
    onChange({ ...condition, valueEnd });
  };

  // Build field options with category prefix for grouping
  const fieldOptions: SimpleDropdownOption[] = fieldConfigs.map((fc) => {
    const isBuilding = fc.field.startsWith('unit.building');
    const prefix = isBuilding ? 'Gebouw · ' : 'Eenheid · ';
    return { value: fc.field, label: `${prefix}${fc.label}` };
  });

  // Build operator options
  const operatorOptions: SimpleDropdownOption[] = availableOperators.map((op) => ({
    value: op,
    label: OPERATOR_LABELS[op] || op,
  }));

  const renderValueInput = () => {
    if (!needsValue) return null;

    const fieldType = selectedFieldConfig?.type || 'string';

    // Enum field → dropdown from enumValues
    if (fieldType === 'enum' && selectedFieldConfig?.enumValues) {
      const enumOptions: SimpleDropdownOption[] = selectedFieldConfig.enumValues.map((ev) => ({
        value: ev.key,
        label: ev.label,
      }));
      return (
        <SimpleDropdown
          value={condition.value != null ? String(condition.value) : ''}
          onChange={(v) => handleValueChange(v)}
          options={enumOptions}
          placeholder="Waarde..."
          triggerClassName="min-w-[140px]"
        />
      );
    }

    // Boolean field
    if (fieldType === 'boolean') {
      const boolOptions: SimpleDropdownOption[] = [
        { value: 'true', label: 'Ja' },
        { value: 'false', label: 'Nee' },
      ];
      return (
        <SimpleDropdown
          value={condition.value != null ? String(condition.value) : ''}
          onChange={(v) => handleValueChange(v === 'true')}
          options={boolOptions}
          placeholder="Waarde..."
          triggerClassName="min-w-[100px]"
        />
      );
    }

    // Number field
    if (fieldType === 'number') {
      return (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            placeholder="Waarde..."
            className="min-w-[100px]"
            value={condition.value != null ? String(condition.value) : ''}
            onChange={(e) => {
              const v = e.target.value;
              handleValueChange(v === '' ? null : Number(v));
            }}
          />
          {isBetween && (
            <>
              <span className="text-xs text-muted-foreground px-1">en</span>
              <Input
                type="number"
                placeholder="Tot..."
                className="min-w-[100px]"
                value={condition.valueEnd != null ? String(condition.valueEnd) : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  handleValueEndChange(v === '' ? '' : Number(v));
                }}
              />
            </>
          )}
        </div>
      );
    }

    // String / relation / default
    return (
      <Input
        placeholder="Waarde..."
        className="min-w-[140px]"
        value={condition.value != null ? String(condition.value) : ''}
        onChange={(e) => handleValueChange(e.target.value || null)}
      />
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Field selector */}
      <SimpleDropdown
        value={condition.field || ''}
        onChange={handleFieldChange}
        options={fieldOptions}
        placeholder="Veld kiezen..."
        triggerClassName="min-w-[160px] max-w-[200px]"
      />

      {/* Operator selector */}
      <SimpleDropdown
        value={condition.operator || ''}
        onChange={handleOperatorChange}
        options={operatorOptions}
        placeholder="Operator..."
        disabled={!condition.field}
        triggerClassName="min-w-[160px] max-w-[200px]"
      />

      {/* Value input */}
      {renderValueInput()}

      {/* Remove button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="text-muted-foreground hover:text-destructive shrink-0"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
};

export default UnitFilterConditionRow;
